import { useAuth } from "@certn/auth";
import {
  Text,
  Card,
  GhostIconButton,
  TextAvatar,
  Stack,
  CertnAvatar,
} from "@certn/cascadia-react";
import { parseName } from "@certn/utils";
import { css } from "@emotion/css";
import { useTranslation } from "react-i18next";

interface Props {
  name?: string;
  email?: string;
  domain?: string;
}

export function LoginDomainCard(props: Props) {
  const { loginWithRedirect } = useAuth();
  const { email, name } = props;

  const { t } = useTranslation();

  const handleButtonClick = () => {
    if (props.domain?.includes("Centric")) {
      loginWithRedirect({
        authorizationParams: {
          prompt: "login",
          screen_hint: "login",
          login_hint: email,
        },
        appState: {
          returnTo: "/",
        },
      });
      return;
    }

    // otherwise just go to the app in question
    return (window.location.href = "https://app.certn.co/");
  };

  const getInitials = (name: string | undefined) => {
    if (!name) {
      return <CertnAvatar size="x-large" />;
    }

    const parsedName = parseName(props.name);
    const initials = parsedName.initials;
    return <TextAvatar label={initials} size="x-large" />;
  };

  return (
    <button className={styles.divbutton} onClick={handleButtonClick}>
      <Card
        padding="space-4"
        className={styles.card}
        background="surface-sunken"
      >
        <Stack orientation="horizontal" align="center" gap="space-3">
          {getInitials(name)}
          <Stack gap="space-1" align="start">
            <Text
              style="global/title-small"
              color="text-base"
              className={styles.title}
            >
              {t("Continue as")} {!name ? email : props.name}
            </Text>
            <Text style="global/body-secondary" color="text-weak">
              {!name ? "" : email}
            </Text>
          </Stack>
        </Stack>
        <Stack>
          <GhostIconButton
            icon="arrow_forward_ios"
            onPress={handleButtonClick}
            className={styles.button}
          />
        </Stack>
      </Card>
    </button>
  );
}

const styles = {
  card: css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderRadius: "var(--border-radius-4)",
    borderWidth: "1px",
    width: "543.5px",
    ":hover": {
      backgroundColor: "var(--background-active)",
      borderColor: "var(--border-button-hover)",
    },
    "@media (max-width: 768px)": {
      width: "100%",
      margin: "0 auto",
    },
  }),
  button: css({
    borderRadius: "var(--border-radius-2)",
  }),
  divbutton: css({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    margin: 0,
    padding: 0,
    border: "none",
    borderRadius: "var(--border-radius-4)",
    cursor: "pointer",
    boxShadow: "var(--shadow-card)",
  }),
  title: css({
    marginLeft: "0",
    "@media (max-width: 768px)": {
      fontWeight: "500",
      fontSize: "16px",
      marginLeft: "var(--space-3)",
    },
  }),
};
